/**
 * Geo Helper - Converts coordinates to city names
 * Calls server-side API endpoint since all-the-cities requires Node.js modules
 */

export interface CityInfo {
  city: string;
  country: string;
  countryCode: string;
}

/**
 * Gets city information from latitude and longitude coordinates
 * Calls the server-side API endpoint
 * @param lat - Latitude
 * @param lon - Longitude
 * @returns City information or null if not found
 */
export async function getCityFromCoords(lat: number, lon: number): Promise<CityInfo | null> {
  try {
    const response = await fetch('/api/get-city-from-coords', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lat, lon }),
    });

    // Check if response is ok
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[GeoHelper] API error (${response.status}):`, errorText);
      return null;
    }

    // Check if response has content
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('[GeoHelper] Unexpected response type:', contentType, text);
      return null;
    }

    const data = await response.json();

    if (data.success && data.city) {
      return {
        city: data.city,
        country: data.country,
        countryCode: data.countryCode,
      };
    }

    return null;
  } catch (error: any) {
    console.error('[GeoHelper] Error getting city from coordinates:', error);
    if (error.message?.includes('JSON')) {
      console.error('[GeoHelper] JSON parse error - response might be empty or malformed');
    }
    return null;
  }
}

